import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiCheck, FiChevronRight, FiClock, FiFilter,
  FiRefreshCw, FiTarget, FiX,
} from 'react-icons/fi';

import { useI18n } from '../i18n/I18nProvider';

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const Card = ({ children, className = '' }) => (
  <section className={`border border-gray-200 bg-white rounded-lg shadow-sm ${className}`}>{children}</section>
);

function useQuestionDeadline(limitMs, questionKey, active, onExpire) {
  const [remaining, setRemaining] = useState(limitMs);
  const callbackRef = useRef(onExpire);
  const deadlineRef = useRef(0);
  const pausedRef = useRef(limitMs);
  callbackRef.current = onExpire;

  useEffect(() => {
    if (!active) return undefined;
    let frame;
    let expired = false;
    deadlineRef.current = performance.now() + limitMs;
    pausedRef.current = limitMs;
    setRemaining(limitMs);

    const tick = () => {
      if (!document.hidden) {
        const next = Math.max(0, deadlineRef.current - performance.now());
        pausedRef.current = next;
        setRemaining(next);
        if (next <= 0 && !expired) {
          expired = true;
          callbackRef.current();
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        pausedRef.current = Math.max(0, deadlineRef.current - performance.now());
      } else {
        deadlineRef.current = performance.now() + pausedRef.current;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [active, limitMs, questionKey]);
  return remaining;
}

const SummaryMetric = ({ label, value, tone = 'gray' }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    gray: 'bg-gray-50 text-gray-800 border-gray-200',
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`}>
      <div className="text-sm opacity-75">{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </div>
  );
};

const WordbookFilterPage = () => {
  const { wordbookId } = useParams();
  const navigate = useNavigate();
  const { t, apiMessage } = useI18n();
  const [phase, setPhase] = useState('loading');
  const [metadata, setMetadata] = useState(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [cursor, setCursor] = useState(-1);
  const [hasMore, setHasMore] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ known: 0, missed: 0, unknown: 0, timedOut: 0 });
  const initialRemainingRef = useRef(0);
  const advanceTimerRef = useRef(null);
  const current = queue[index];
  const limitMs = metadata?.time_limit_ms || 7000;

  const loadBatch = useCallback(async (after = -1, initial = false) => {
    setError('');
    const response = await fetch(
      `/api/student/wordbooks/${wordbookId}/filter-quiz?after=${after}&limit=40`,
      { headers: authHeaders() },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(apiMessage(data, 'Failed to load word filter.'));
    if (initial) {
      initialRemainingRef.current = data.remaining_count || 0;
      setMetadata(data);
    }
    setQueue(Array.isArray(data.items) ? data.items : []);
    setIndex(0);
    setCursor(data.next_after ?? after);
    setHasMore(Boolean(data.has_more));
    return data;
  }, [apiMessage, wordbookId]);

  useEffect(() => {
    let active = true;
    loadBatch(-1, true)
      .then((data) => {
        if (!active) return;
        setPhase((data.remaining_count || 0) > 0 ? 'intro' : 'complete');
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message);
        setPhase('error');
      });
    return () => {
      active = false;
      window.clearTimeout(advanceTimerRef.current);
    };
  }, [loadBatch]);

  const finishOrLoad = useCallback(async () => {
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      setFeedback(null);
      return;
    }
    if (hasMore) {
      try {
        setPhase('loading-next');
        const data = await loadBatch(cursor, false);
        setFeedback(null);
        setPhase((data.items || []).length ? 'quiz' : 'complete');
      } catch (err) {
        setError(err.message);
        setPhase('quiz');
      }
      return;
    }
    setFeedback(null);
    setPhase('complete');
  }, [cursor, hasMore, index, loadBatch, queue.length]);

  const scheduleAdvance = useCallback((delay = 520) => {
    window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = window.setTimeout(finishOrLoad, delay);
  }, [finishOrLoad]);

  const chooseAnswer = useCallback(async (answer) => {
    if (!current || feedback || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`/api/student/wordbooks/${wordbookId}/filter-answer`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ word: current.word, answer }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiMessage(data, 'Could not check this answer.'));
      if (data.correct) {
        setStats((value) => ({ ...value, known: value.known + 1 }));
        setFeedback({ kind: 'correct', selected: answer });
      } else {
        setStats((value) => ({ ...value, missed: value.missed + 1 }));
        setFeedback({ kind: 'incorrect', selected: answer, answer: data.correct_answer || current.answer });
      }
      scheduleAdvance(data.correct ? 380 : 720);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [apiMessage, current, feedback, scheduleAdvance, submitting, wordbookId]);

  const markUnknown = useCallback((timedOut = false) => {
    if (!current || feedback || submitting) return;
    setStats((value) => ({
      ...value,
      unknown: value.unknown + 1,
      timedOut: value.timedOut + (timedOut ? 1 : 0),
    }));
    setFeedback({ kind: timedOut ? 'timeout' : 'unknown', answer: current.answer });
    scheduleAdvance(720);
  }, [current, feedback, scheduleAdvance, submitting]);

  const remaining = useQuestionDeadline(
    limitMs,
    `${current?.word || 'none'}-${index}`,
    phase === 'quiz' && Boolean(current) && !feedback && !submitting,
    () => markUnknown(true),
  );

  const restartRemaining = async () => {
    setPhase('loading');
    setStats({ known: 0, missed: 0, unknown: 0, timedOut: 0 });
    try {
      const data = await loadBatch(-1, true);
      setPhase((data.remaining_count || 0) > 0 ? 'intro' : 'complete');
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  };

  const attempted = stats.known + stats.missed + stats.unknown;
  const total = initialRemainingRef.current;
  const progress = total > 0 ? Math.min(100, Math.round((attempted / total) * 100)) : 100;

  if (phase === 'loading' || phase === 'loading-next') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center text-gray-600">
          <FiRefreshCw className="animate-spin mx-auto mb-3 text-indigo-600" size={26} />
          {t(phase === 'loading-next' ? 'Loading the next words...' : 'Preparing your word filter...')}
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <Card className="max-w-xl mx-auto p-7 text-center">
        <FiX className="mx-auto text-red-500" size={32} />
        <h1 className="text-xl font-bold mt-3">{t('Word filter could not start')}</h1>
        <p className="text-red-600 mt-2">{error}</p>
        <button onClick={restartRemaining} className="mt-5 px-5 py-2.5 rounded-md bg-indigo-600 text-white font-semibold">
          {t('Try again')}
        </button>
      </Card>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/student/word-overview')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-5">
          <FiArrowLeft /> {t('Back to Word Overview')}
        </button>
        <Card className="overflow-hidden">
          <div className="p-6 sm:p-9 border-b bg-emerald-50">
            <div className="text-xs uppercase font-semibold text-emerald-700">{t('Quick progress cleanup')}</div>
            <h1 data-i18n-skip className="text-3xl font-bold text-gray-950 mt-2">{metadata?.title}</h1>
            <p className="text-gray-700 mt-3 max-w-2xl">
              {t('Quickly remove words you already know from the learning queue. You have seven seconds for each four-choice meaning question.')}
            </p>
          </div>
          <div className="p-6 sm:p-9">
            <div className="grid sm:grid-cols-3 gap-3">
              <SummaryMetric label={t('Words in this wordbook')} value={metadata?.total_count || 0} />
              <SummaryMetric label={t('Already completed')} value={metadata?.mastered_count || 0} tone="green" />
              <SummaryMetric label={t('Ready to filter')} value={metadata?.remaining_count || 0} tone="amber" />
            </div>
            <div className="mt-6 border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-950">
              <strong>{t('What happens when you answer correctly?')}</strong>
              <p className="mt-1">{t('The word is marked as learned and all eight reviews are completed. Wrong, unknown, and timed-out words stay in your learning queue.')}</p>
            </div>
            <div className="mt-7 flex flex-wrap justify-end gap-3">
              <button onClick={() => navigate('/student/word-overview')} className="px-5 py-3 border rounded-md font-semibold text-gray-700">
                {t('Not now')}
              </button>
              <button onClick={() => setPhase('quiz')} className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-md font-semibold hover:bg-emerald-700">
                <FiFilter /> {t('Start filtering')} <FiChevronRight />
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (phase === 'complete') {
    const left = Math.max(0, total - stats.known);
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="overflow-hidden">
          <div className="p-7 sm:p-10 text-center border-b bg-emerald-50">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-600 text-white flex items-center justify-center"><FiCheck size={28} /></div>
            <h1 className="text-3xl font-bold mt-4">{t(total ? 'Filtering complete' : 'This wordbook is already complete')}</h1>
            <p className="text-gray-600 mt-2">{t(total ? 'Your progress now reflects what you already knew.' : 'There are no unmastered words to filter right now.')}</p>
          </div>
          <div className="p-6 sm:p-9">
            {total > 0 && (
              <div className="grid sm:grid-cols-3 gap-3">
                <SummaryMetric label={t('Filtered as known')} value={stats.known} tone="green" />
                <SummaryMetric label={t('Still worth learning')} value={stats.missed + stats.unknown} tone="amber" />
                <SummaryMetric label={t('Remaining in this wordbook')} value={left} />
              </div>
            )}
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {left > 0 && (
                <button onClick={restartRemaining} className="inline-flex items-center gap-2 px-5 py-3 border border-indigo-300 text-indigo-700 rounded-md font-semibold hover:bg-indigo-50">
                  <FiRefreshCw /> {t('Filter the remaining words again')}
                </button>
              )}
              <button onClick={() => navigate('/student/word-overview')} className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white rounded-md font-semibold hover:bg-indigo-700">
                <FiArrowLeft /> {t('Back to Word Overview')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!current) return null;
  const seconds = Math.max(0, remaining / 1000).toFixed(1);
  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-4 mb-4">
        <button onClick={() => navigate('/student/word-overview')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <FiArrowLeft /> {t('Exit filter')}
        </button>
        <div className="text-sm text-gray-600">{attempted + 1} / {total}</div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700"><FiTarget /> {t('Choose the best meaning')}</div>
          <div className={`inline-flex items-center gap-2 font-mono font-bold ${remaining < 1500 ? 'text-red-600' : 'text-gray-700'}`}>
            <FiClock /> {seconds}s
          </div>
        </div>
        <div className="h-1 bg-gray-100">
          <div className={`h-full ${remaining < 1500 ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${(remaining / limitMs) * 100}%` }} />
        </div>
        <div className="p-6 sm:p-10">
          <div data-i18n-skip className="text-center text-4xl sm:text-5xl font-bold text-gray-950 break-words">{current.word}</div>
          <p className="text-center text-gray-500 mt-3">{t('Which meaning fits this word?')}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            {current.choices.map((choice) => {
              const selected = feedback?.selected === choice;
              const correctChoice = feedback && feedback.answer === choice;
              let classes = 'border-gray-300 hover:border-indigo-500 hover:bg-indigo-50';
              if (selected && feedback?.kind === 'correct') classes = 'border-emerald-500 bg-emerald-50 text-emerald-900';
              else if (selected && feedback?.kind === 'incorrect') classes = 'border-red-400 bg-red-50 text-red-900';
              else if (correctChoice) classes = 'border-emerald-400 bg-emerald-50 text-emerald-900';
              return (
                <button
                  key={choice}
                  disabled={Boolean(feedback) || submitting}
                  onClick={() => chooseAnswer(choice)}
                  className={`min-h-[88px] p-4 text-left border-2 rounded-lg font-medium transition-colors disabled:cursor-default ${classes}`}
                >
                  {choice}
                </button>
              );
            })}
          </div>
          <button
            disabled={Boolean(feedback) || submitting}
            onClick={() => markUnknown(false)}
            className="mt-4 w-full min-h-[54px] border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-60"
          >
            {t("I don't know this word")}
          </button>
          <div className="min-h-[52px] mt-4 text-center">
            {submitting && <span className="inline-flex items-center gap-2 text-gray-600"><FiRefreshCw className="animate-spin" /> {t('Checking...')}</span>}
            {feedback?.kind === 'correct' && <span className="font-semibold text-emerald-700">{t('Known word cleared. All reviews completed.')}</span>}
            {feedback?.kind === 'incorrect' && <span className="text-red-700">{t('Not cleared. Correct meaning:')} <strong data-i18n-skip>{feedback.answer}</strong></span>}
            {(feedback?.kind === 'unknown' || feedback?.kind === 'timeout') && (
              <span className="text-amber-800">{t(feedback.kind === 'timeout' ? 'Time is up. Kept for learning:' : 'Kept for learning:')} <strong data-i18n-skip>{feedback.answer}</strong></span>
            )}
            {error && <div className="text-red-600 mt-1">{error}</div>}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default WordbookFilterPage;
